import {
  Button,
  Separator,
  Row,
  Cell,
  Skeleton,
  useOverlay,
  useStatusOverlay,
  H5,
  H6,
  Subheading,
  H2,
  Paragraph,
  IconButton,
  QuickGlanceOverlay,
} from '@fs/zion-ui'
import { ArrowCaret, NoticeInfo } from '@fs/zion-icon'
import React, { useEffect, useState } from 'react'
import { baseUrl } from '@fs/zion-config'
import { useLazyQuery } from '@apollo/client'
import { getPreference } from '@fs/zion-preferences'
import { useQueryParams } from '@fs/zion-router'
import { useFeatureFlag } from '@fs/zion-flags'

import { useGroupData, useDetermineMode, useSubmitCaiReview } from '../../hooks'
import transl from './GroupDataHeader.transl'
import RecordMetadataReviewInstructionsOverlay, {
  disclaimerPreferenceName,
} from './RecordMetadataReviewInstructionsOverlay'
import FinishRecordMetadataReviewOverlay from './FinishRecordMetadataReviewOverlay'
import queries from './queries'
import InformationPanel from './InformationPanel'

function EditButtonGroupDataHeader() {
  const {
    group: { types, internalFields, imageApids },
    initialGroup: { childGroups, imageApids: initialImageApids },
    itemNumber,
    itemCount,
  } = useGroupData()
  const isVolumeSet = types?.includes('VOLUME_SET')
  const isSplitGroup = Boolean(childGroups?.length > 0)
  const overlay = useOverlay()

  // Calculate tooltip data for the info icon
  const initialLength = initialImageApids?.length
  const firstImage = internalFields?.apid
  const startIndex = React.useMemo(
    () => (firstImage != null ? initialImageApids?.findIndex((apid) => apid === firstImage) || 0 + 1 : null),
    [firstImage, initialImageApids]
  )
  const numberOfImages = imageApids?.length
  const endIndex = startIndex + numberOfImages - 1

  return (
    <>
      {!isVolumeSet && (
        <H2 size="H4" data-heading>
          <Subheading />
        </H2>
      )}
      <Separator size="xs" />
      <Row alignY="middle" gutters="xs">
        <Cell>
          <Paragraph size="sm">
            {isSplitGroup ? transl.subHeading(itemNumber, itemCount) : transl.wholeGroup()}
          </Paragraph>
        </Cell>
        {isSplitGroup && (
          <Cell>
            <IconButton
              Icon={NoticeInfo}
              size="xs"
              color="blue"
              emphasis="low"
              title={transl.tooltipAria()}
              onClick={overlay.handleClick}
            />
          </Cell>
        )}
      </Row>
      <Separator size="xxs" />
      {isSplitGroup && (
        <QuickGlanceOverlay {...overlay} title={transl.tooltipLabel(itemNumber, itemCount)} maxWidth="300px">
          <Paragraph size="sm">{transl.tooltipDescription(itemNumber, startIndex, endIndex, initialLength)}</Paragraph>
        </QuickGlanceOverlay>
      )}
    </>
  )
}

function RecordMetadataReviewHeader({ onEditClick }) {
  const { query } = useQueryParams()
  const { mode } = query
  const [showReviewInstructions, setShowReviewInstructions] = useState()

  const openedOverlay = React.useRef(false)
  const overlay = useOverlay()
  const finishRecordMetadataReviewOverlay = useOverlay()

  const showStatusOverlay = useStatusOverlay()
  const {
    group: { id: groupId },
  } = useGroupData()
  const [fetchFolderStatus, { loading: statusLoading }] = useLazyQuery(queries.recordMetadataReviewStatus(), {
    fetchPolicy: 'no-cache',
  })
  const [resettingCAIReview, setResettingCAIReview] = React.useState()
  const { resetCaiReview } = useSubmitCaiReview({
    onError: () => {
      showStatusOverlay({
        message: transl.errorMessage(),
        type: 'error',
        dismissable: true,
      })
    },
    onSuccess: () => {
      window.location.assign(`${baseUrl}/records/home/review`)
    },
    setLoading: setResettingCAIReview,
  })

  useEffect(() => {
    const checkForPreference = async () => {
      setShowReviewInstructions(!(await getPreference(disclaimerPreferenceName)))
    }
    if (mode === 'review') checkForPreference()
  }, [mode])

  useEffect(() => {
    if (showReviewInstructions && !openedOverlay?.current && overlay && !(overlay.isOpen && overlay.transitioning)) {
      overlay.open()
      openedOverlay.current = true
    }
  }, [openedOverlay, overlay, showReviewInstructions])

  return (
    <>
      <RecordMetadataReviewInstructionsOverlay {...{ overlay, setShowReviewInstructions }} />
      <FinishRecordMetadataReviewOverlay {...{ finishRecordMetadataReviewOverlay }} />

      <Button
        data-testid="back-to-cai-dashboard-button"
        disabled={resettingCAIReview || statusLoading}
        Icon={ArrowCaret}
        iconDirection="backward"
        onClick={async () => {
          const folderStatus = await fetchFolderStatus({ variables: { rmsId: groupId } })
          const isInProgress = folderStatus?.data?.recordMetadataReviewStatus?.status === 'In Progress'
          if (isInProgress) {
            resetCaiReview()
          } else {
            window.location.assign(`${baseUrl}/records/home/review`)
          }
        }}
      >
        {transl.recordMetadataReviewBackToDashboardButton()}
      </Button>
      <H6 size="H5" data-heading>
        {transl.recordMetadataReviewTitle()}
      </H6>
      <H5 supplementary>
        {transl.recordMetadataReviewModifyHeading()}
        <Subheading>{transl.recordMetadataReviewModifySubHeading()}</Subheading>
      </H5>
      <Separator size="xs" />
      <Row alignY="middle">
        <Cell>
          <Button disabled={resettingCAIReview} emphasis="medium" onClick={onEditClick}>
            {transl.recordMetadataReviewEditMetadataButton()}
          </Button>
        </Cell>
      </Row>

      <Separator />

      <H5 supplementary>
        {transl.recordMetadataReviewIsCorrectHeading()}
        <Subheading>{transl.recordMetadataReviewIsCorrectSubHeading()}</Subheading>
      </H5>
      <Separator size="xs" />
      <Row alignY="middle">
        <Cell>
          <Button disabled={resettingCAIReview} emphasis="medium" onClick={finishRecordMetadataReviewOverlay.open}>
            {transl.recordMetadataReviewIsCorrectButton()}
          </Button>
        </Cell>
      </Row>

      <Separator />
    </>
  )
}

export default function GroupDataHeader({ hideEditButton = true, onEditClick }) {
  const {
    initialGroup: { types },
    loading,
  } = useGroupData()
  const { showRecordMetadataReview } = useDetermineMode()
  const showPrevAndNextButtons = useFeatureFlag('lightyear_sharedImageViewer_showPrevAndNextItemButtons')

  if (loading) return <LoadingView />

  const isVolumeSet = types?.includes('VOLUME_SET')
  const showPrevNext = showPrevAndNextButtons.isOn && !hideEditButton && !isVolumeSet
  const showEditButton = !hideEditButton

  return (
    <>
      {showRecordMetadataReview ? (
        <RecordMetadataReviewHeader
          {...{
            onEditClick,
          }}
        />
      ) : (
        <>
          <EditButtonGroupDataHeader />

          {showEditButton && <InformationPanel onEditClick={onEditClick} showPrevNext={showPrevNext} />}
        </>
      )}
    </>
  )
}

function LoadingView() {
  return (
    <>
      <Separator size="xxs" />
      <Row gutters="xxs">
        <Cell growX>
          <Skeleton.H5 subheadingLineCount={1} />
        </Cell>
        <Cell>
          <Skeleton.Button minWidth="70px" />
        </Cell>
      </Row>
      <Separator size="xxs" />
    </>
  )
}
